import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import LockedPaneCard from '@/components/lock/LockedPaneCard';
import { scopedRosterRole, useAllManageableGroups } from '@/hooks/useManageableGroups';
import RosterSidebar, { type RosterSidebarOrg } from './sidebar/RosterSidebar';
import type { RosterSidebarGroup } from './sidebar/RosterGroupListItem';
import RosterPage from './RosterPage';
import OrgRosterPage from './OrgRosterPage';
import MemberDetailPage from './MemberDetailPage';
import ScopedMemberDetailPage from './ScopedMemberDetailPage';

const LAST_VIEW_KEY = 'flock_roster_last_view';

function loadLastView(): string | null {
  try {
    return localStorage.getItem(LAST_VIEW_KEY);
  } catch {
    return null;
  }
}

function saveLastView(view: string) {
  try {
    localStorage.setItem(LAST_VIEW_KEY, view);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Roster workspace: cross-org sidebar + content pane (PrayersLayout pattern).
 * Routes: /roster (auto-select last view), /roster/org/:orgId[/member/:memberId]
 * (org-level roster, leadership only), /roster/group/:groupId[/member/:memberId].
 */
export default function RosterLayout() {
  const { orgId: orgParam, groupId: groupParam, memberId } = useParams();
  const navigate = useNavigate();
  const {
    organizations,
    currentOrganization,
    currentGroup,
    switchOrganization,
    isSwitchingOrg,
    setCurrentGroup,
    isLoading,
  } = useAuth();
  const { byOrg: manageableByOrg, isLoading: manageableLoading } = useAllManageableGroups();
  const { isOrgLocked } = useOrgLockState();

  // Resolve the selected org entry — only valid with a leadership role there
  const selectedOrg = useMemo((): RosterSidebarOrg | null => {
    if (!orgParam) return null;
    const membership = organizations.find((o) => o.organization_id === orgParam);
    if (!membership || scopedRosterRole(membership) === null) return null;
    return { orgId: membership.organization_id, orgName: membership.organization.name };
  }, [orgParam, organizations]);

  // Resolve the selected group across ALL orgs: memberships first, then
  // role-scoped groups (an org_admin can open groups they're not in)
  const selectedGroup = useMemo((): RosterSidebarGroup | null => {
    if (!groupParam) return null;
    for (const membership of organizations) {
      const group = membership.groups?.find((g) => g.id === groupParam);
      if (group) {
        return {
          id: group.id,
          name: group.name,
          orgId: membership.organization_id,
          orgName: membership.organization.name,
          imageUrl: group.group_image_url,
        };
      }
    }
    for (const membership of organizations) {
      const group = manageableByOrg[membership.organization_id]?.find(
        (g) => g.id === groupParam
      );
      if (group) {
        return {
          id: group.id,
          name: group.name,
          orgId: membership.organization_id,
          orgName: membership.organization.name,
          imageUrl: group.group_image_url,
        };
      }
    }
    return null;
  }, [groupParam, organizations, manageableByOrg]);

  const selectedKey = orgParam ? `org:${orgParam}` : (groupParam ?? null);
  const orgsLoaded = !isLoading && organizations.length > 0;

  // Auto-select on bare /roster: last-used view → current group → org entry
  // for the current org → first group anywhere
  useEffect(() => {
    if (orgParam || groupParam || !orgsLoaded) return;
    const last = loadLastView();
    if (last?.startsWith('org:')) {
      const lastOrgId = last.slice(4);
      const membership = organizations.find((o) => o.organization_id === lastOrgId);
      if (membership && scopedRosterRole(membership) !== null) {
        navigate(`/roster/org/${lastOrgId}`, { replace: true });
        return;
      }
    } else if (last) {
      const lastGroupExists = organizations.some(
        (o) =>
          o.groups?.some((g) => g.id === last) ||
          manageableByOrg[o.organization_id]?.some((g) => g.id === last)
      );
      if (lastGroupExists) {
        navigate(`/roster/group/${last}`, { replace: true });
        return;
      }
    }
    if (currentGroup) {
      navigate(`/roster/group/${currentGroup.id}`, { replace: true });
      return;
    }
    const currentMembership = organizations.find(
      (o) => o.organization_id === currentOrganization?.id
    );
    if (currentMembership && scopedRosterRole(currentMembership) !== null) {
      navigate(`/roster/org/${currentMembership.organization_id}`, { replace: true });
      return;
    }
    const firstGroup = organizations.flatMap((o) => o.groups ?? [])[0];
    if (firstGroup) {
      navigate(`/roster/group/${firstGroup.id}`, { replace: true });
    }
  }, [
    orgParam,
    groupParam,
    orgsLoaded,
    organizations,
    manageableByOrg,
    currentGroup,
    currentOrganization,
    navigate,
  ]);

  // Heal unknown ids (left the group / lost the role / bad deep link). Wait
  // for manageable groups before declaring a group unknown — an admin-only
  // group only resolves once those queries land.
  useEffect(() => {
    if (orgParam && orgsLoaded && !selectedOrg) {
      navigate('/roster', { replace: true });
      return;
    }
    if (groupParam && orgsLoaded && !manageableLoading && !selectedGroup) {
      navigate('/roster', { replace: true });
    }
  }, [orgParam, groupParam, orgsLoaded, manageableLoading, selectedOrg, selectedGroup, navigate]);

  // Persist the active view for next visit
  useEffect(() => {
    if (selectedOrg) saveLastView(`org:${selectedOrg.orgId}`);
    else if (selectedGroup) saveLastView(selectedGroup.id);
  }, [selectedOrg, selectedGroup]);

  // Deep links / refreshes can land on a cross-org selection without going
  // through the select handlers — rotate the org context so the pane can
  // mount. If the org context later moves away from a selection we were
  // ALREADY aligned with (the user switched orgs in another tab's rail),
  // FOLLOW them instead of switching back.
  //
  // alignedKeyRef is only written in the aligned state — never inside the
  // heal branch — so StrictMode's double-invoked effects can't flip the
  // heal into a follow. Heal attempts are capped to avoid a retry loop if
  // the org switch keeps failing (e.g. membership revoked server-side).
  const alignedKeyRef = useRef<string | null>(null);
  const healAttemptsRef = useRef<Record<string, number>>({});
  useEffect(() => {
    const selection = selectedOrg
      ? { key: `org:${selectedOrg.orgId}`, orgId: selectedOrg.orgId, groupId: undefined }
      : selectedGroup
        ? { key: selectedGroup.id, orgId: selectedGroup.orgId, groupId: selectedGroup.id }
        : null;
    if (!selection || !currentOrganization || isSwitchingOrg) return;

    if (selection.orgId === currentOrganization.id) {
      alignedKeyRef.current = selection.key;
      healAttemptsRef.current = {};
      return;
    }

    if (alignedKeyRef.current === selection.key) {
      // User moved the org context out from under the URL — follow them.
      navigate(currentGroup ? `/roster/group/${currentGroup.id}` : '/roster', {
        replace: true,
      });
      return;
    }

    // Deep link / refresh onto a cross-org selection: heal by rotating the org.
    const attempts = healAttemptsRef.current[selection.key] ?? 0;
    if (attempts >= 2) {
      navigate('/roster', { replace: true });
      return;
    }
    healAttemptsRef.current[selection.key] = attempts + 1;
    switchOrganization(selection.orgId, selection.groupId).catch((e) =>
      console.warn('[Roster] Org switch for deep link failed:', e)
    );
  }, [
    selectedOrg,
    selectedGroup,
    currentOrganization,
    isSwitchingOrg,
    switchOrganization,
    currentGroup,
    navigate,
  ]);

  const handleSelectGroup = useCallback(
    async (group: RosterSidebarGroup) => {
      navigate(`/roster/group/${group.id}`);

      // Cross-org group: rotate org context (claims) so REST calls work.
      if (group.orgId !== currentOrganization?.id) {
        try {
          await switchOrganization(group.orgId, group.id);
        } catch (e) {
          console.warn('[Roster] Org switch on group select failed:', e);
        }
      } else {
        // Keep the app-level current group in sync (membership groups only —
        // a role-scoped group the user isn't in can't become currentGroup)
        const membership = organizations.find((o) => o.organization_id === group.orgId);
        const fullGroup = membership?.groups.find((g) => g.id === group.id);
        if (fullGroup && fullGroup.id !== currentGroup?.id) setCurrentGroup(fullGroup);
      }
    },
    [
      navigate,
      currentOrganization?.id,
      switchOrganization,
      organizations,
      setCurrentGroup,
      currentGroup?.id,
    ]
  );

  const handleSelectOrg = useCallback(
    async (org: RosterSidebarOrg) => {
      navigate(`/roster/org/${org.orgId}`);

      if (org.orgId !== currentOrganization?.id) {
        try {
          await switchOrganization(org.orgId);
        } catch (e) {
          console.warn('[Roster] Org switch on org select failed:', e);
        }
      }
    },
    [navigate, currentOrganization?.id, switchOrganization]
  );

  const selectionOrgId = selectedOrg?.orgId ?? selectedGroup?.orgId ?? null;
  const aligned = selectionOrgId !== null && currentOrganization?.id === selectionOrgId;

  return (
    <div className="flex h-full">
      <RosterSidebar
        selectedKey={selectedKey}
        onSelectOrg={handleSelectOrg}
        onSelectGroup={handleSelectGroup}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        {selectionOrgId && isOrgLocked(selectionOrgId) ? (
          // Paused org: placeholder instead of firing doomed roster fetches.
          <LockedPaneCard organizationId={selectionOrgId} />
        ) : selectionOrgId && !aligned ? (
          <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
            Switching organization…
          </div>
        ) : selectedOrg ? (
          memberId ? (
            <ScopedMemberDetailPage org={selectedOrg} memberId={memberId} />
          ) : (
            <OrgRosterPage key={selectedOrg.orgId} org={selectedOrg} />
          )
        ) : selectedGroup ? (
          memberId ? (
            <MemberDetailPage group={selectedGroup} memberId={memberId} />
          ) : (
            <RosterPage key={selectedGroup.id} group={selectedGroup} />
          )
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
            {orgsLoaded ? 'Select a group to see its roster.' : 'Loading…'}
          </div>
        )}
      </div>
    </div>
  );
}
